import { useState, useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface FaqItem {
  question: string;
  answer: ReactNode;
}

interface FaqAccordionProps {
  items: FaqItem[];
  className?: string;
  /** Two-column layout (default false) */
  twoCol?: boolean;
}

function FaqEntry({ item, open, onToggle, id }: { item: FaqItem; open: boolean; onToggle: () => void; id: string }) {
  return (
    <div className="warm-card overflow-hidden" style={{ border: '1px solid var(--warm-border)' }}>
      <button
        id={`faq-btn-${id}`}
        aria-expanded={open}
        aria-controls={`faq-body-${id}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-sm font-medium transition-colors"
        style={{ color: 'var(--warm-text)', background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <span>{item.question}</span>
        <span className="shrink-0 text-lg leading-none transition-transform duration-300" style={{ color: 'var(--warm-muted)', transform: open ? 'rotate(45deg)' : 'none' }}>+</span>
      </button>
      <div id={`faq-body-${id}`} role="region" aria-labelledby={`faq-btn-${id}`} className={cn('faq-body', open && 'open')}>
        <div className="px-5 pb-5 text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
          {item.answer}
        </div>
      </div>
    </div>
  );
}

export function FaqAccordion({ items, className, twoCol = false }: FaqAccordionProps) {
  const uid = useId();
  const [openIdx, setOpenIdx] = useState<number>(0);

  const toggle = (i: number) => setOpenIdx((prev) => (prev === i ? -1 : i));

  if (!twoCol) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        {items.map((item, i) => (
          <FaqEntry key={i} item={item} open={openIdx === i} onToggle={() => toggle(i)} id={`${uid}-${i}`} />
        ))}
      </div>
    );
  }

  const col1 = items.filter((_, i) => i % 2 === 0);
  const col2 = items.filter((_, i) => i % 2 === 1);

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-2', className)}>
      <div className="flex flex-col gap-2">
        {col1.map((item, ci) => {
          const i = ci * 2;
          return <FaqEntry key={i} item={item} open={openIdx === i} onToggle={() => toggle(i)} id={`${uid}-${i}`} />;
        })}
      </div>
      <div className="flex flex-col gap-2">
        {col2.map((item, ci) => {
          const i = ci * 2 + 1;
          return <FaqEntry key={i} item={item} open={openIdx === i} onToggle={() => toggle(i)} id={`${uid}-${i}`} />;
        })}
      </div>
    </div>
  );
}
